import { useTranslation } from "@impero-com/common/utils/i18n";
import { Modal } from "@impero-com/ui";
import { Checkbox } from "antd";

import { Footer } from "components/modals/Footer";
import { formatAccessRightRole } from "utils/accessRights";

import { removalLabels, RemovalPlan } from "./removal";
import * as styles from "./RemoveAccessRightModal.css";

interface RemoveAccessRightModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
    user: string;
    plan: RemovalPlan;
    understood: boolean;
    onUnderstoodChange: (understood: boolean) => void;
}

export function RemoveAccessRightModal({
    open,
    onClose,
    onConfirm,
    loading,
    user,
    plan: { scope, entityIds, selectedCount, accessRights },
    understood,
    onUnderstoodChange,
}: RemoveAccessRightModalProps) {
    const { t } = useTranslation();
    const labels = removalLabels(t, scope);

    const changes = [
        [t("AccessAndAssignment_RemoveAccessRight_User"), user],
        [t("AccessAndAssignment_RemoveAccessRight_Action"), labels.action],
        [
            t("AccessAndAssignment_RemoveAccessRight_AffectedEntities"),
            t("AccessAndAssignment_RemoveAccessRight_AffectedEntitiesCount", {
                affected: entityIds.length,
                selected: selectedCount,
            }),
        ],
        [
            t("AccessAndAssignment_RemoveAccessRight_CurrentAccessLevels"),
            accessRights.map(formatAccessRightRole).join(", "),
        ],
    ];

    return (
        <Modal
            variant="content"
            open={open}
            onClose={onClose}
            title={labels.action}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={onConfirm}
                    confirmLabel={t(
                        "AccessAndAssignment_RemoveAccessRight_Remove"
                    )}
                    destructive
                    loading={loading}
                    okDisabled={!understood}
                />
            }
        >
            <div className={styles.body}>
                <div className={styles.changes}>
                    <span className={styles.changesHeading}>
                        {t("AccessAndAssignment_RemoveAccessRight_Changes")}
                    </span>
                    {changes.map(([label, value]) => (
                        <div key={label} className={styles.changesRow}>
                            <span className={styles.changesLabel}>{label}</span>
                            <span className={styles.changesValue}>{value}</span>
                        </div>
                    ))}
                </div>
                <Checkbox
                    checked={understood}
                    onChange={(event) =>
                        onUnderstoodChange(event.target.checked)
                    }
                >
                    {labels.understand(user, entityIds.length)}
                </Checkbox>
            </div>
        </Modal>
    );
}
